import React, { useState } from "react";
import {
    AppBar, Toolbar, Box, IconButton, Drawer, Typography,
    Button, Menu, MenuItem, Chip
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import SidebarMenu from "./SidebarMenu";
import { getMenuConfig, getCustomColors } from "../config/menuConfigs";
import { Business, Settings } from "@mui/icons-material";
import UserMenu from "./UserMenu";

export default function TopBarLayout({ children }) {
    const navigate = useNavigate();
    const { userProfile, isAdmin, switchContext } = useAuth();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [customColors, setCustomColors] = useState(getCustomColors());
    const [adminMenuAnchor, setAdminMenuAnchor] = useState(null);

    // Contextos
    const availableContexts = userProfile?.contexts || [
        { name: userProfile?.companyName || "Empresa", type: "empresa" },
        { name: "Usuario Particular", type: "particular" }
    ];
    const currentContext = userProfile?.activeContext || availableContexts[0]?.name;
    const drawerWidth = 280;

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleColorsChange = (newColors) => {
        setCustomColors(newColors);
    };

    const handleAdminMenuOpen = (event) => {
        setAdminMenuAnchor(event.currentTarget);
    };

    const handleAdminMenuClose = () => {
        setAdminMenuAnchor(null);
    };

    const handleMenuTypeChange = (newType) => {
        handleAdminMenuClose();
        
        // Navegar a la ruta correspondiente
        switch (newType) {
        case "business":
            navigate("/admin/business");
            break;
        case "system":
            navigate("/admin/system");
            break;
        case "user":
        default:
            navigate("/dashboard");
            break;
        }
    };

    // Configuración de menú - SOLO para usuario normal
    const menuConfig = getMenuConfig("user");

    const drawer = (
        <SidebarMenu
            sections={menuConfig.sections}
            colors={customColors}
            title={menuConfig.title}
            showColorPicker={false} // Usuarios normales no pueden personalizar colores
            onColorsChange={handleColorsChange}
            defaultColors={menuConfig.defaultColors}
        />
    );

    return (
        <Box sx={{ display: "flex" }}>
            {/* AppBar */}
            <AppBar 
                position="fixed" 
                sx={{ 
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: "white",
                    color: "primary.main",
                    boxShadow: 1
                }}
            >
                <Toolbar>
                    {/* Botón de menú para móvil */}
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: "none" } }}
                    >
                        <Box component="span" sx={{ fontSize: "1.5rem" }}>☰</Box>
                    </IconButton>

                    {/* Logo */}
                    <Box 
                        sx={{ 
                            flexGrow: 1, 
                            display: "flex", 
                            alignItems: "center",
                            cursor: "pointer"
                        }}
                        onClick={() => navigate("/dashboard")}
                    >
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                fontWeight: "bold",
                                color: "primary.main"
                            }}
                        >
                            CAPIVIO
                        </Typography>
                    </Box>

                    {/* Selector de tipo de menú para admins */}
                    {isAdmin && (
                        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Business />}
                                onClick={handleAdminMenuOpen}
                                sx={{ 
                                    borderColor: "primary.main",
                                    color: "primary.main",
                                    "&:hover": {
                                        borderColor: "primary.dark",
                                        backgroundColor: "primary.light"
                                    }
                                }}
                            >
                                <Chip 
                                    label="Administración"
                                    size="small"
                                    color="primary"
                                    sx={{ ml: 1, height: 20 }}
                                />
                            </Button>
                            
                            <Menu
                                anchorEl={adminMenuAnchor}
                                open={Boolean(adminMenuAnchor)}
                                onClose={handleAdminMenuClose}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                }}
                            >
                                <MenuItem onClick={() => handleMenuTypeChange("business")}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Business />
                                        <Typography>Admin de Negocio</Typography>
                                    </Box>
                                </MenuItem>
                                <MenuItem onClick={() => handleMenuTypeChange("system")}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Settings />
                                        <Typography>Admin de Sistema</Typography>
                                    </Box>
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}

                    {/* Menú de usuario profesional */}
                    <UserMenu
                        userProfile={userProfile}
                        currentContext={currentContext}
                        availableContexts={availableContexts}
                        onSwitchContext={switchContext}
                        onProfile={() => navigate("/profile")}
                        onSettings={() => navigate("/settings")}
                        onOffboarding={() => navigate("/offboarding")}
                        onLogout={() => { window.localStorage.clear(); window.location.href = "/login"; }}
                    />
                </Toolbar>
            </AppBar>

            {/* Sidebar */}
            <Box
                component="nav"
                sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
            >
                {/* Sidebar para desktop */}
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        "& .MuiDrawer-paper": { 
                            boxSizing: "border-box", 
                            width: drawerWidth,
                            border: "none"
                        }
                    }}
                    open
                >
                    <Toolbar /> {/* Espacio para AppBar */}
                    {drawer}
                </Drawer>

                {/* Sidebar para móvil */}
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true // Mejor rendimiento en móvil
                    }}
                    sx={{
                        display: { xs: "block", sm: "none" },
                        "& .MuiDrawer-paper": { 
                            boxSizing: "border-box", 
                            width: drawerWidth 
                        }
                    }}
                >
                    {drawer}
                </Drawer>
            </Box>

            {/* Contenido principal */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    width: { sm: `calc(100% - ${drawerWidth}px)` },
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f5"
                }}
            >
                <Toolbar /> {/* Espacio para AppBar */}
                {children}
            </Box>
        </Box>
    );
} 